import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../auth/AuthProvider';
import { useSettings } from '../hooks/useSettings';
import { useFosContext } from '../hooks/useFosContext';
import { db } from '../firebase';
import { isAdmin, hasCouncilFullRead, visibleDistricts } from '../lib/visibility';
import { recName, type AppSettings, type NamedRecord, type UserProfile } from '../types';
import { PRODUCT_NAME } from '../branding';
import logo from '../assets/compass-logo.png';

export interface AppContext {
  /** 'All' or a district id. */
  districtFilter: string;
  /** Set the top-bar district filter (e.g. from a dashboard drill-down). */
  setDistrictFilter: (v: string) => void;
  settings: AppSettings;
  profile: UserProfile;
  /** Districts this user may pick (records). */
  districts: NamedRecord[];
}

export function useAppContext(): AppContext {
  return useOutletContext<AppContext>();
}

const NAV = [
  { to: '/', label: 'Dashboard', end: true },
  { to: '/strategy', label: 'Strategic Plan' },
  { to: '/goals', label: 'Goals' },
  { to: '/initiatives', label: 'Initiatives' },
  { to: '/actions', label: 'Actions' },
  { to: '/calendar', label: 'Calendar' },
  { to: '/units', label: 'Unit Health' },
  { to: '/contacts', label: 'Contacts' },
  { to: '/org', label: 'Org Chart' },
  { to: '/commissioners', label: 'Commissioners' },
  { to: '/unit-map', label: 'Unit Map' },
  { to: '/communications', label: 'Communications' },
];

// Pages where the "council strategy cascades..." tagline actually applies —
// everywhere else it's just noise unrelated to that screen.
const TAGLINE_PATHS = new Set(['/goals', '/strategy']);

export default function Layout() {
  const { profile, firebaseUser, signOut, tenantIds, switchTenant } = useAuth();
  const { settings } = useSettings();
  const { pathname } = useLocation();
  const [districtFilter, setDistrictFilter] = useState('All');
  const [navOpen, setNavOpen] = useState(false);
  const closeNav = () => setNavOpen(false);
  // The server decides who sees Friends of Scouting: administrators (to turn it on or use it) and anyone with donor access.
  const { context: fosContext } = useFosContext();
  const showFos = !!fosContext && (fosContext.enabled ? fosContext.canViewDonors || fosContext.canAdministerSettings || fosContext.capabilities.viewProgress : fosContext.canAdministerSettings);

  // Phase 5D: display names for every tenant this account belongs to —
  // tenantIds[0] (the active one, for the sidebar tagline) and, when there's
  // more than one, the rest too (for the switcher's own options). Reads
  // tenants/{id} directly per id rather than a collection query — the new
  // isMyTenant() rule (firestore.rules) only allows a self-scoped get, not
  // a list, and this is at most a couple of documents.
  const [tenantNames, setTenantNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (tenantIds.length === 0) return;
    let cancelled = false;
    void Promise.all(
      tenantIds.map(async (id) => {
        try {
          const snap = await getDoc(doc(db, 'tenants', id));
          return [id, (snap.data()?.name as string | undefined) ?? id] as const;
        } catch {
          return [id, id] as const;
        }
      }),
    ).then((pairs) => {
      if (!cancelled) setTenantNames(Object.fromEntries(pairs));
    });
    return () => {
      cancelled = true;
    };
  }, [tenantIds]);
  const [switching, setSwitching] = useState(false);

  const districts = useMemo(
    () => visibleDistricts(profile, settings.districts),
    [profile, settings.districts],
  );

  if (!profile) return null;
  const admin = isAdmin(profile);
  const councilWide = hasCouncilFullRead(profile);
  const pickable = admin ? settings.districts : districts;
  // Admin and council-wide members both browse via the top-bar picker, so
  // the banner follows whichever district (or "All") is currently
  // selected. Everyone else has no picker — always their one home district.
  const followsPicker = admin || councilWide;

  const ctx: AppContext = {
    districtFilter,
    setDistrictFilter,
    settings,
    profile,
    districts: pickable,
  };

  return (
    <div className="app">
      <header className="mobile-topbar">
        <button
          type="button"
          className="nav-toggle"
          aria-label="Open menu"
          aria-expanded={navOpen}
          onClick={() => setNavOpen(true)}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
            <path
              d="M3 6h18M3 12h18M3 18h18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <img className="topbar-logo" src={logo} alt="" />
        <span className="topbar-title">{PRODUCT_NAME}</span>
      </header>

      {navOpen && <div className="nav-backdrop" onClick={closeNav} />}
      <aside className={`sidebar${navOpen ? ' open' : ''}`}>
        <div className="brand">
          <img className="brand-logo" src={logo} alt={PRODUCT_NAME} />
          <div>
            <h2>{PRODUCT_NAME}</h2>
            <p>{tenantNames[tenantIds[0]] ?? ''}</p>
          </div>
        </div>
        <nav onClick={closeNav}>
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) => (isActive ? 'active' : '')}
            >
              {n.label}
            </NavLink>
          ))}
          {showFos && (
            <NavLink to="/fos" className={({ isActive }) => (isActive ? 'active' : '')}>
              Friends of Scouting
            </NavLink>
          )}
          {admin && (
            <NavLink to="/settings" className={({ isActive }) => (isActive ? 'active' : '')}>
              Settings
            </NavLink>
          )}
        </nav>
        <div className="who">
          <div>{firebaseUser?.displayName ?? profile.displayName ?? profile.email}</div>
          <div style={{ opacity: 0.7 }}>
            {profile.role}
            {profile.districtId ? ` · ${recName(settings.districts, profile.districtId)}` : ''}
            {councilWide ? ' · Council-wide view' : ''}
          </div>
          {tenantIds.length > 1 && (
            <select
              aria-label="Switch tenant"
              value={tenantIds[0]}
              disabled={switching}
              onChange={async (e) => {
                const next = e.target.value;
                if (next === tenantIds[0]) return;
                setSwitching(true);
                try {
                  await switchTenant(next);
                } finally {
                  setSwitching(false);
                }
              }}
            >
              {tenantIds.map((id) => (
                <option key={id} value={id}>
                  {tenantNames[id] ?? id}
                </option>
              ))}
            </select>
          )}
          <button onClick={signOut}>Sign out</button>
        </div>
      </aside>
      <main>
        <div className="top">
          <div>
            <h1>{PRODUCT_NAME}</h1>
            <div className="district-banner">
              {followsPicker
                ? districtFilter === 'All'
                  ? pathname === '/org'
                    ? 'Council'
                    : 'All Districts'
                  : `${recName(settings.districts, districtFilter)} District`
                : `${recName(settings.districts, profile.districtId ?? '')} District`}
            </div>
            {TAGLINE_PATHS.has(pathname) && (
              <div className="subtitle">
                Council strategy cascades into measurable goals and district-level action.
              </div>
            )}
          </div>
          {/* The district picker filters the other screens; Friends of Scouting does not use it, so it is not shown there. */}
          {(admin || pickable.length > 1) && !pathname.startsWith('/fos') && (
            <select value={districtFilter} onChange={(e) => setDistrictFilter(e.target.value)}>
              <option value="All">{pathname === '/org' ? 'Council' : 'All Districts'}</option>
              {pickable.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          )}
        </div>
        <Outlet context={ctx} />
      </main>
    </div>
  );
}
